/**
 * Campaign canvas types — mirror of the backend campaign-state projection
 * (services/conversation-service/app/campaign_state.py).
 *
 * Message history is the source of truth; the canvas renders the derived
 * projection returned by GET /conversations/{id}/campaign-state and applies
 * `canvas` SSE patches optimistically between fetches.
 */

export type Stage = 'brief' | 'strategy' | 'creative' | 'launch' | 'live';
export type Gate = 'strategy' | 'creative' | 'launch';

export const STAGES: Stage[] = ['brief', 'strategy', 'creative', 'launch', 'live'];
export const STAGE_LABELS: Record<Stage, string> = {
  brief: 'Brief',
  strategy: 'Strategy',
  creative: 'Creative',
  launch: 'Launch',
  live: 'Live',
};

export interface Rendition {
  id: string;
  platform: string;
  format?: string;
  enabled?: boolean;
  assetUrl?: string;
}

export interface ConceptVariation {
  id: string;
  assetUrl?: string;
  assetId?: string | null;
}

export interface ConceptVersion {
  variations?: ConceptVariation[];
  selectedVariation?: string;
  /** The regeneration direction that replaced these takes */
  direction?: string | null;
}

export interface ConceptCopy {
  // `headlines`/`bodies` (plural) drive Meta dynamic creative (DCO) on PLAIN images —
  // Meta assembles/optimizes across them. Ignored for finished ads (bakedCopy), whose
  // copy is already in the pixels. Singular `headline`/`body` remain the fallback.
  meta?: { headline?: string; body?: string; headlines?: string[]; bodies?: string[] };
  google?: { headlines?: string[]; descriptions?: string[] };
  pinterest?: { title?: string; description?: string };
  tiktok?: { script?: string };
  twitter?: { tweet?: string; headline?: string };
}

/**
 * One card of a carousel ad. A card is a SLOT in a sequence (distinct image shown
 * alongside the others) — not to be confused with a concept's `variations`, which
 * are alternative takes of a single image. A card's image may REUSE an existing
 * concept image (via `sourceConceptId` + `assetUrl`) or be uploaded/generated on
 * its own; reuse is a default suggestion, never forced (see roadmap media note).
 */
export interface CarouselCard {
  id: string;
  assetUrl?: string;
  assetId?: string | null;
  headline?: string;
  link?: string;
  /** Where this card's image came from, so mixed uploaded+generated is supported. */
  source?: 'generated' | 'uploaded';
  /** If the image was reused from another concept, its id (reference, copy-on-edit). */
  sourceConceptId?: string;
}

export interface CreativeConcept {
  id: string;
  concept?: string;
  rationale?: string;
  included?: boolean;
  source?: 'generated' | 'uploaded';
  /** 'image' (default) or 'video'. Video is user-provided only for now — a carousel
   *  is always images. Drives the publisher's image vs video creative branch. */
  mediaType?: 'image' | 'video';
  /** True = a FINISHED ad with copy baked into the image (Ad Studio, or a user's own
   *  finished creative). Publishes as a plain single image with NO dynamic creative,
   *  since Meta-assembled text would collide with the in-image headline. Undefined/
   *  false = a plain image → DCO-eligible (Meta assembles/optimizes the copy). */
  bakedCopy?: boolean;
  /** When present (>= 2), this concept publishes as a CAROUSEL of these ordered
   *  cards instead of a single-image ad. */
  cards?: CarouselCard[];
  copyVariants?: number;
  /** Per-concept ad copy — takes precedence over shared messaging for publishing */
  copy?: ConceptCopy;
  /** The exact image-generation prompt sent for this concept (stored at enqueue time) */
  imagePrompt?: string;
  renditions?: Rendition[];
  /** Image generation lifecycle (async, per-concept) */
  status?: 'generating' | 'ready' | 'failed' | null;
  variations?: ConceptVariation[];
  selectedVariation?: string;
  versions?: ConceptVersion[];
  jobId?: string | null;
  pendingDirection?: string | null;
  failureReason?: string;
  /** Per-format image crop/pan adjustments applied at publish time. Key = PreviewFormat string. */
  adjustments?: Record<string, {
    crop: { x_frac: number; y_frac: number; w_frac: number; h_frac: number };
    textLayers: unknown[];
    targetWidth?: number;
    targetHeight?: number;
  }>;
}

export interface LaunchAccount {
  platform: string;
  accountId?: string;
  accountName?: string;
  enabled?: boolean;
  /** OAuth connection backing this account (set once accounts are loaded) */
  connectionId?: string;
  isDefault?: boolean;
  pageId?: string;
  pageName?: string;
  boardId?: string;
}

export interface CampaignState {
  name?: string;
  objective?: string;
  /**
   * PER-PLATFORM delivery mode, keyed by platform ('meta', 'google'). 'manual'
   * (default) = Admeru builds precise ad sets. 'advantage_plus' = AI-managed (Meta
   * Advantage+ Sales / Google PMax) run WITH Admeru's guardrails. Per-platform so
   * choosing Advantage+ on Meta does NOT force Performance Max on Google. The
   * compiler applies each platform's guardrails and refuses to manual if the
   * conversion signal it needs is missing.
   */
  campaignType?: Record<string, 'manual' | 'advantage_plus'>;
  budget?: {
    total?: number;
    currency?: string;
    durationDays?: number;
    split?: Record<string, number>;
  };
  audience?: {
    summary?: string;
    targeting?: Record<string, unknown>;
    /**
     * Who to keep OUT of prospecting. `existingCustomers` defaults ON — the
     * single biggest waste vector in broad/Advantage+ delivery (see roadmap
     * Phase 1). `audiences` are additional semantic exclusion labels the LLM or
     * user supplies (e.g. "recent purchasers"), resolved to real audiences at
     * compile time.
     */
    exclusions?: { existingCustomers?: boolean; audiences?: string[] };
  };
  messaging?: {
    meta?: { headline?: string; body?: string; headlines?: string[]; bodies?: string[] };
    google?: { headlines?: string[]; descriptions?: string[] };
    pinterest?: { title?: string; description?: string };
    tiktok?: { script?: string; text?: string };
    twitter?: { tweet?: string; text?: string };
  };
  creativeSet?: CreativeConcept[];
  launch?: {
    mode?: 'paid' | 'organic';
    accounts?: LaunchAccount[];
    /** source 'owner': the owner picked another date than their monthly plan's. */
    timing?: { type?: 'now' | 'scheduled'; at?: string | null; source?: 'plan' | 'owner' };
    organicSelection?: string[];
  };
  [key: string]: unknown;
}

/** Shape returned by GET /conversations/{id}/campaign-state */
export interface DerivedCampaignState {
  stage: Stage;
  state: CampaignState;
  approvals: Gate[];
  next_gate: Gate | null;
  message_id?: string;
}

/**
 * Derives the set of platforms actively included in this campaign.
 * Checks budget.split first (authoritative for paid campaigns), then
 * falls back to checking for non-empty messaging content per platform.
 * Returns an empty Set only when there is genuinely no signal — callers
 * should treat an empty set as "show everything" to stay backward-compatible.
 */
export function getStrategyPlatforms(state: CampaignState): Set<string> {
  const platforms = new Set<string>();
  const split = state.budget?.split ?? {};
  const msg = state.messaging ?? {};

  // Budget split is authoritative — each key is a platform in the strategy.
  Object.keys(split).forEach(p => platforms.add(p));

  // Messaging content is a secondary signal (organic strategies have no split).
  // Only count a platform when it has real string content, not just {}.
  if (msg.meta?.headline || msg.meta?.body) platforms.add('meta');
  const gmsg = (msg as any).google_ads ?? msg.google;
  if ((gmsg?.headlines?.length ?? 0) > 0 || (gmsg?.descriptions?.length ?? 0) > 0) {
    platforms.add('google');
    platforms.add('google_ads');
  }
  if (msg.pinterest?.title || msg.pinterest?.description) platforms.add('pinterest');
  if ((msg as any).tiktok?.script || (msg as any).tiktok?.text) platforms.add('tiktok');
  if ((msg as any).twitter?.tweet || (msg as any).twitter?.text) platforms.add('twitter');

  return platforms;
}

/** Payload of the `canvas` SSE event emitted while streaming */
export interface CanvasSSEEvent {
  state_patch?: Record<string, unknown> | null;
  suggestions?: string[];
}
